/* eslint-disable react-refresh/only-export-components */
import dayjs from 'dayjs'
import React, {
  createContext,
  FC,
  PropsWithChildren,
  useContext,
  useState,
} from 'react'
import { SlotInfo } from 'react-big-calendar'

import { getSeasonPriceKey, isWeekEnd } from './get-price-key-from-date'
import {
  isBigCalendarClickAllowed,
  isBigCalendarSelectAllowed,
} from './is-big-calendar-click-allowed'
import {
  HouseAvailabilityGlobalInputDto,
  HouseAvailabilityGlobalStateEnum,
  HouseAvailabilityIndividualInputDto,
  HouseContext,
  HousePriceTypeEnum,
} from '../../../../../../../lib/graphql'
import { Nullable } from '../../../../../../../types'
import { convertTimezonedDateToIsoAtMidnight } from '../../../../../../../utils/date-helpers'
import {
  HouseContextGlobal,
  HouseContextIndividual,
  isHouseGlobal,
  isHouseIndividual,
} from '../../../../helpers/assert-house-price-type'

/**
 * Global
 */
export type BigCalendarFormDataGlobal = Pick<
  HouseAvailabilityGlobalInputDto,
  'date' | 'houseId'
> & {
  corporateEvening: WithState<
    HouseAvailabilityGlobalInputDto['corporateEvening']
  >
  nightlyTrip: WithState<HouseAvailabilityGlobalInputDto['nightlyTrip']>
  studyDay: WithState<HouseAvailabilityGlobalInputDto['studyDay']>
  house: HouseContextGlobal
}
export type BigCalendarFormDataGlobalSlots = Slots<'global'>
export type BigCalendarFormDataGlobalCell = Cell<'global'>
export type BigCalendarModalGlobalFormProps =
  | BigCalendarFormDataGlobalCell
  | BigCalendarFormDataGlobalSlots

/**
 * Individual
 */
export type BigCalendarFormDataIndividual =
  HouseAvailabilityIndividualInputDto & {
    house: HouseContextIndividual
  }

export type BigCalendarFormDataIndividualSlots = Slots<'individual'>
export type BigCalendarFormDataIndividualCell = Cell<'individual'>
export type BigCalendarModalIndividualFormProps =
  | BigCalendarFormDataIndividualCell
  | BigCalendarFormDataIndividualSlots

/**
 * Utility Types
 */
type WithState<T> = T & { state: HouseAvailabilityGlobalStateEnum }
type WithSlotsDates<T> = T extends
  | BigCalendarFormDataGlobal
  | BigCalendarFormDataIndividual
  ? Omit<T, 'date'> & { start: Date; end: Date }
  : never

type Cell<K extends 'global' | 'individual'> = {
  type: 'cell'
  kind: K
  formValues: K extends 'global'
    ? BigCalendarFormDataGlobal
    : BigCalendarFormDataIndividual
}
type Slots<K extends 'global' | 'individual'> = {
  type: 'slots'
  kind: K
  formValues: WithSlotsDates<
    K extends 'global'
      ? BigCalendarFormDataGlobal
      : BigCalendarFormDataIndividual
  >
}

export type BigCalendarModalFormProps =
  | BigCalendarModalIndividualFormProps
  | BigCalendarModalGlobalFormProps

type SelectSlotProps = { slotInfo: SlotInfo; house: HouseContext }

export interface BigCalencarFormContextProps {
  modalFormProps: Nullable<BigCalendarModalFormProps>
  handleOnModalClose: () => void
  handleOnSelectSlot: (slot: SelectSlotProps) => void
}
export function useBigCalendarForm(): BigCalencarFormContextProps {
  const [modalFormProps, setModalProps] =
    useState<Nullable<BigCalendarModalFormProps>>(null)

  const handleOnModalClose = () => {
    setModalProps(null)
  }

  /**
   * Handle both click and select
   */
  const handleOnSelectSlot = ({ house, slotInfo }: SelectSlotProps) => {
    const { action, start, end } = slotInfo

    if (action === 'click' && isBigCalendarClickAllowed({ date: start })) {
      handleOnCellOpen({ house, start })
    }

    if (action === 'select' && isBigCalendarSelectAllowed({ start, end })) {
      handleOnSlotOpen({ end, house, start })
    }
  }

  /**
   * When selecting a range of cell
   */
  const handleOnSlotOpen = ({
    end: _end,
    start: _start,
    house,
  }: {
    end: Date
    start: Date
    house: HouseContext
  }) => {
    const end = convertTimezonedDateToIsoAtMidnight(
      dayjs(_end).add(-1, 'minute').toDate(),
    )
    const start = convertTimezonedDateToIsoAtMidnight(dayjs(_start).toDate())

    const isOnlyOneDay = dayjs(start).isSame(dayjs(end), 'day')
    if (isOnlyOneDay) {
      return handleOnCellOpen({ house, start })
    }

    if (isHouseGlobal(house)) {
      return setModalProps({
        type: 'slots',
        kind: 'global',
        formValues: {
          end,
          start,
          house,
          houseId: house._id,
          corporateEvening: {
            isEnabled: house.options.corporateEvening.isEnabled,
            state: HouseAvailabilityGlobalStateEnum.FREE,
          },
          nightlyTrip: {
            options: { price: null },
            isEnabled: house.options.nightlyTrip.isEnabled,
            state: HouseAvailabilityGlobalStateEnum.FREE,
          },
          studyDay: {
            state: HouseAvailabilityGlobalStateEnum.FREE,
            isEnabled: house.options.studyDay.isEnabled,
            options: { price: null },
          },
        },
      })
    }

    if (isHouseIndividual(house)) {
      return setModalProps({
        type: 'slots',
        kind: 'individual',
        formValues: {
          end,
          start,
          house,
          houseId: house._id,
          corporateEvening: {
            isEnabled: house.options.corporateEvening.isEnabled,
          },
          nightlyTrip: {
            options: { sharedBedroomPrice: null, singleBedroomPrice: null },
            isEnabled: house.options.nightlyTrip.isEnabled,
          },
          studyDay: {
            options: {
              fullDayPrice: null,
              halfDayPrice: null,
              residentialPrice: null,
            },
            isEnabled: house.options.studyDay.isEnabled,
          },
        },
      })
    }
  }

  /**
   * When clicking on a single cell
   */
  const handleOnCellOpen = ({
    start: _start,
    house,
  }: {
    house: HouseContext
    start: Date
  }) => {
    const date = convertTimezonedDateToIsoAtMidnight(dayjs(_start).toDate())

    const availability =
      house.availabilities?.find(({ date: _date, __typename }) => {
        return dayjs(date).isSame(dayjs(_date), 'day')
      }) ?? null

    const isNotICalendar =
      availability?.__typename === 'HouseAvailabilityGlobalOutputDto' ||
      availability?.__typename === 'HouseAvailabilityIndividualOutputDto'

    const isAvailability =
      dayjs(availability?.date).isSame(date) && isNotICalendar
    if (isAvailability) {
      const { corporateEvening, nightlyTrip, studyDay } = availability

      const kind =
        house.priceType === HousePriceTypeEnum.GLOBAL ? 'global' : 'individual'

      return setModalProps({
        type: 'cell',
        kind,
        formValues: {
          house,
          date,
          houseId: house._id,
          corporateEvening,
          nightlyTrip,
          studyDay,
        },
      } as BigCalendarModalFormProps)
    }

    if (isHouseGlobal(house)) {
      const priceKey = getSeasonPriceKey({ date, options: house.options })

      const nightlyTripPrice =
        house.options.nightlyTrip.options?.seasonalPrices[0][
          isWeekEnd({ date }) ? 'weekEnd' : 'weekDay'
        ][priceKey]

      const studyDayPrice =
        house.options.studyDay.options?.seasonalPrices[0][priceKey]

      const optionsNightlyTrip: BigCalendarFormDataGlobal['nightlyTrip'] = {
        state: HouseAvailabilityGlobalStateEnum.FREE,
        options: { price: nightlyTripPrice },
        isEnabled: house.options.nightlyTrip.isEnabled,
      }
      const optionsStudyDay: BigCalendarFormDataGlobal['studyDay'] = {
        state: HouseAvailabilityGlobalStateEnum.FREE,
        options: { price: studyDayPrice },
        isEnabled: house.options.studyDay.isEnabled,
      }

      const optionsCorporateEvening: BigCalendarFormDataGlobal['corporateEvening'] =
        {
          state: HouseAvailabilityGlobalStateEnum.FREE,
          isEnabled: house.options.corporateEvening.isEnabled,
        }

      return setModalProps({
        type: 'cell',
        kind: 'global',
        formValues: {
          house,
          date,
          houseId: house._id,
          corporateEvening: optionsCorporateEvening,
          nightlyTrip: optionsNightlyTrip,
          studyDay: optionsStudyDay,
        },
      })
    }

    if (isHouseIndividual(house)) {
      const priceKey = getSeasonPriceKey({ date, options: house.options })

      const singleBedroomPrice =
        house.options.nightlyTrip.options?.seasonalPrices[0].singleBedroom[
          priceKey
        ]

      const sharedBedroomPrice =
        house.options.nightlyTrip.options?.seasonalPrices[0].sharedBedroom[
          priceKey
        ]

      const fullDayPrice =
        house.options.studyDay.options?.seasonalPrices[0].fullDay[priceKey]

      const halfDayPrice =
        house.options.studyDay.options?.seasonalPrices[0].halfDay[priceKey]

      const residentialPrice =
        house.options.studyDay.options?.seasonalPrices[0].residential[priceKey]

      const nightlyTrip: BigCalendarFormDataIndividual['nightlyTrip'] = {
        options: { sharedBedroomPrice, singleBedroomPrice },
        isEnabled: house.options.nightlyTrip.isEnabled,
      }
      const studyDay: BigCalendarFormDataIndividual['studyDay'] = {
        options: { fullDayPrice, halfDayPrice, residentialPrice },
        isEnabled: house.options.studyDay.isEnabled,
      }

      const corporateEvening: BigCalendarFormDataIndividual['corporateEvening'] =
        {
          isEnabled: house.options.corporateEvening.isEnabled,
        }

      return setModalProps({
        type: 'cell',
        kind: 'individual',
        formValues: {
          house,
          date,
          houseId: house._id,
          corporateEvening,
          nightlyTrip,
          studyDay,
        },
      })
    }
  }

  return { modalFormProps, handleOnModalClose, handleOnSelectSlot }
}

const BigCalencarFormContext = createContext<BigCalencarFormContextProps>(
  {} as BigCalencarFormContextProps,
)

export const BigCalencarFormProvider: FC<PropsWithChildren> = ({
  children,
}) => {
  const value = useBigCalendarForm()

  return (
    <BigCalencarFormContext.Provider value={value}>
      {children}
    </BigCalencarFormContext.Provider>
  )
}

// eslint-disable-next-line react-refresh/only-export-components
export const useBigCalencarFormContext = () => {
  return useContext(BigCalencarFormContext)
}
